import { Assets } from "pixi.js";

import { SHOWITEM_PHOTO_FRAME_URL, STAMP_ASSETS } from "../assets";

import {
  resolveAssetUrl,
  resolveStoryAssetByKey,
  resolveStoryAudioByKey,
  resolveStoryCharacterAssetByKey,
  type StoryAssetFamily,
} from "./asset";
import { resolveCharacterSelection } from "./characterRef";
import {
  ANIMTEXT_MAIN_FONT,
  DIALOG_FONT,
  loadFont,
  SAMI_FONTS,
  type FontSpec,
} from "./font";
import { parseScript } from "./parser";
import { DIALOG_FRAME_URL } from "./renderer";

import type { Context } from "../context";
import type { ParsedLine, StoryCharacterFaceAsset } from "./types";

export type { StoryCharacterFaceAsset } from "./types";

export interface ContextAssetManifest {
  faceAssets: StoryCharacterFaceAsset[];
  urls: string[];
}

function argAsString(raw: unknown): string {
  return typeof raw === "string" ? raw.trim() : "";
}

function resolveGroupedImageSelection(
  imageGroup: string,
  cgGroup: string,
  isBackgroundCommand: boolean,
): { asBackground: boolean; group: string } | null {
  if (imageGroup)
    return { asBackground: isBackgroundCommand, group: imageGroup };
  if (cgGroup) return { asBackground: false, group: cgGroup };
  return null;
}

function addImageUrl(
  urls: Set<string>,
  rawKey: string,
  family: StoryAssetFamily,
): void {
  const key = rawKey.trim();
  if (!key) return;

  const rawUrl = resolveStoryAssetByKey(key, family);
  if (!rawUrl) return;
  urls.add(resolveAssetUrl(rawUrl));
}

function addAudioUrl(
  urls: Set<string>,
  context: Context,
  rawKey: string,
): void {
  const key = rawKey.trim();
  if (!key) return;

  const rawUrl = resolveStoryAudioByKey(key, context.audioVariables);
  if (!rawUrl) return;
  urls.add(rawUrl);
}

function resolveCharacterUrl(rawKey: string): string | null {
  const key = rawKey.trim();
  if (!key) return null;

  const rawUrl = resolveStoryCharacterAssetByKey(key);
  if (!rawUrl) return null;
  return resolveAssetUrl(rawUrl);
}

function addCharacterUrl(urls: Set<string>, rawKey: string): string | null {
  const url = resolveCharacterUrl(rawKey);
  if (!url) return null;
  urls.add(url);
  return url;
}

/**
 * Native provenance: the active panels' `IContainsResRefs` collectors,
 * including `AVGShowItemPanel.InternalResRefCollector.GatherResRefs`,
 * `AVGCgItemPanel.InternalResRefCollector.GatherResRefs`, and the image /
 * background / character panel collectors.
 *
 * Ports which script commands declare image dependencies. A single eager PIXI
 * batch substitutes for native asset-reference collection and loading.
 *
 */
function addScriptImageUrls(urls: Set<string>, lines: ParsedLine[]): void {
  for (const line of lines) {
    if (line.kind !== "command") continue;

    const image = argAsString(line.args.image);
    if (line.command === "background") {
      addImageUrl(urls, image, "background");
      continue;
    }
    if (line.command === "avgdisplay") {
      const rawStyle = argAsString(line.args.style);
      const styleMap: Record<string, string> = { 5: "bg", 7: "character" };
      const style = styleMap[rawStyle] ?? rawStyle;
      const name = argAsString(line.args.name);
      if (style === "bg") addImageUrl(urls, name, "background");
      else if (style === "character") addCharacterUrl(urls, name);
      continue;
    }
    if (
      line.command === "image" ||
      line.command === "showitem" ||
      line.command === "cgitem" ||
      line.command === "blocker"
    ) {
      addImageUrl(urls, image, "image");
      continue;
    }
    if (line.command === "interlude") {
      const type = argAsString(line.args.type);
      const numericType =
        typeof line.args.type === "number" ? line.args.type : Number(type);
      const name = argAsString(line.args.name);
      if (type === "bg" || numericType === 2)
        addImageUrl(urls, name, "background");
      else if (type === "uichar" || numericType === 1)
        addImageUrl(urls, name, "cutin");
      continue;
    }

    if (
      line.command === "gridbg" ||
      line.command === "verticalbg" ||
      line.command === "largebg" ||
      line.command === "largeimg"
    ) {
      const imageGroup = argAsString(line.args.imagegroup);
      const cgGroup = argAsString(line.args.cggroup);
      const groupSelection = resolveGroupedImageSelection(
        imageGroup,
        cgGroup,
        line.command.endsWith("bg"),
      );
      if (!groupSelection) continue;
      for (const key of groupSelection.group.split("/"))
        addImageUrl(
          urls,
          key,
          groupSelection.asBackground ? "background" : "image",
        );
    }
  }
}

/**
 * Native provenance: `Torappu.AVG.InternalMusicRefCollector` and
 * `Torappu.AVG.InternalSoundRefCollector.GatherResRefs`.
 *
 * Ports music key/intro and sound key discovery; browser preload timing and
 * progress reporting are web-only behavior.
 *
 */
function addScriptAudioUrls(
  urls: Set<string>,
  context: Context,
  lines: ParsedLine[],
): void {
  for (const line of lines) {
    if (line.kind !== "command") continue;

    if (line.command !== "playmusic" && line.command !== "playsound") continue;

    addAudioUrl(urls, context, argAsString(line.args.key));
    if (line.command === "playmusic")
      addAudioUrl(urls, context, argAsString(line.args.intro));
  }
}

function addCharacterUrls(
  urls: Set<string>,
  context: Context,
  lines: ParsedLine[],
): StoryCharacterFaceAsset[] {
  const faceAssets = new Map<string, StoryCharacterFaceAsset>();
  const addFaceGroup = (
    link: Context["linkMap"][string],
    groupIndex: number,
    baseUrl: string,
    usedExpression?: string,
  ): void => {
    const group = link.groups[groupIndex];
    if (group?.mode !== "face_overlay") return;

    for (const candidate of link.array) {
      if (
        candidate.group !== groupIndex ||
        !("face" in candidate) ||
        !candidate.face
      )
        continue;

      const faceUrl = resolveCharacterUrl(candidate.face);
      if (!faceUrl) continue;

      const key = `${baseUrl}\0${faceUrl}\0${group.faceRect.x},${group.faceRect.y},${group.faceRect.w},${group.faceRect.h}`;
      const previous = faceAssets.get(key);
      faceAssets.set(key, {
        baseUrl,
        expression: previous?.expression ?? candidate.name,
        faceRect: group.faceRect,
        faceUrl,
        used: Boolean(previous?.used || candidate.name === usedExpression),
      });
    }
  };

  for (const line of lines) {
    if (line.kind !== "command") continue;

    const refs: string[] = [];
    if (line.command === "charslot") refs.push(argAsString(line.args.name));
    else if (line.command === "character")
      refs.push(argAsString(line.args.name), argAsString(line.args.name2));
    else if (
      line.command === "interlude" &&
      (line.args.type === 3 || argAsString(line.args.type) === "char")
    )
      refs.push(argAsString(line.args.name));
    else continue;

    for (const nameRef of refs) {
      if (!nameRef) continue;

      const resolved = resolveCharacterSelection(context.linkMap, nameRef);
      if (!resolved) continue;

      const link = context.linkMap[resolved.base];
      if (!link) continue;

      const item = link.array.find(
        (entry) => entry.name === resolved.expression,
      );
      if (!item) continue;

      if (item.group === -1 && "image" in item && item.image) {
        const imageUrl = addCharacterUrl(urls, item.image);
        if (imageUrl) {
          for (const [groupIndex, group] of link.groups.entries()) {
            if (
              group.mode === "face_overlay" &&
              resolveCharacterUrl(group.base) === imageUrl
            )
              addFaceGroup(link, groupIndex, imageUrl);
          }
        }
        continue;
      }

      if (!("face" in item) || !item.face || item.group < 0) continue;

      const group = link.groups[item.group];
      if (group?.mode !== "face_overlay") continue;

      const usedFaceUrl = addCharacterUrl(urls, item.face);
      const baseUrl = addCharacterUrl(urls, group.base);
      if (!usedFaceUrl || !baseUrl) continue;
      addFaceGroup(link, item.group, baseUrl, item.name);
    }
  }

  return [...faceAssets.values()];
}

function parseContextScript(context: Context): ParsedLine[] {
  return parseScript(context.scriptText ?? context.script);
}

function buildContextAssetManifest(
  context: Context,
  lines: ParsedLine[],
): ContextAssetManifest {
  const urls = new Set<string>();
  if (context.charMap) {
    for (const rawUrl of Object.values(context.charMap))
      urls.add(resolveAssetUrl(rawUrl));
  }

  const faceAssets = addCharacterUrls(urls, context, lines);

  addScriptAudioUrls(urls, context, lines);
  addScriptImageUrls(urls, lines);

  return { faceAssets, urls: [...urls] };
}

export function collectContextAssetManifest(
  context: Context,
): ContextAssetManifest {
  return buildContextAssetManifest(context, parseContextScript(context));
}

export function collectContextAssetUrls(context: Context): string[] {
  return collectContextAssetManifest(context).urls;
}

function collectPreloadAssetUrls(
  context: Context,
  lines: ParsedLine[],
): string[] {
  const urls = new Set(buildContextAssetManifest(context, lines).urls);

  // 固定 UI 资源（对话框上下黑条纹理），随每次 preload 一起预热进 pixi Assets 缓存，
  // renderer 在 createUi 时再 Assets.load 即可命中缓存。它属于播放器内置资源，
  // 不应出现在面向用户的剧情资源列表中。
  urls.add(DIALOG_FRAME_URL);

  // animtext `group_location_stamp` 的 7 张内置贴片同理。native 侧
  // AVGDisplayableExecutor.InternalResRefCollector.GatherResRefs 对 ANIMATE_TEXT
  // 落入空分支、不登记任何资源引用（native LoadAsset 同步，印章当帧出现）；
  // web 的 Assets.load 异步，首次播放会晚 1-2 帧，故在剧本确实使用 animtext 时
  // 一并预热，让面板内 Assets.load 命中缓存。属 web 适配，非 native 行为移植。
  if (usesCommand(lines, "animtext")) {
    for (const url of Object.values(STAMP_ASSETS)) urls.add(url);
  }

  // showitem photo slot 的九宫格相框同理：native 是场景内置贴图，web 首次
  // Assets.load 走网络，会把第一次 showitem 的淡入整体推迟，故剧本用到
  // showitem 时一并预热。属 web 适配。
  if (usesCommand(lines, "showitem")) urls.add(SHOWITEM_PHOTO_FRAME_URL);

  return [...urls];
}

function usesCommand(lines: ParsedLine[], command: string): boolean {
  return lines.some(
    (line) => line.kind === "command" && line.command === command,
  );
}

/**
 * 判定与 runtime 的 `spellsticker` 分支一致：`style` 缺省为 sami（不区分大小
 * 写），`action=hide` 只隐藏不显示。
 */
function showsSamiSpellSticker(lines: ParsedLine[]): boolean {
  return lines.some((line) => {
    if (line.kind !== "command" || line.command !== "spellsticker")
      return false;
    const { action = "show", style = "sami" } = line.args;
    return (
      String(action).toLowerCase() !== "hide" &&
      String(style).toLowerCase() === "sami"
    );
  });
}

/**
 * 剧本要用的字体：对话字体恒需要，animtext 与萨米 spellsticker 的字体只在剧本
 * 确实用到时才加载。native 字体随 story.unity 常驻，按需加载属 web 适配。
 */
function collectPreloadFonts(lines: ParsedLine[]): FontSpec[] {
  const fonts = [DIALOG_FONT];
  if (usesCommand(lines, "animtext")) fonts.push(ANIMTEXT_MAIN_FONT);
  if (showsSamiSpellSticker(lines)) fonts.push(...SAMI_FONTS);
  return fonts;
}

// 路径前缀/大小写核对属于开发期诊断,线上每次播放刷几十条日志只是噪音。
// 资源加载失败仍然照常 warn,那是用户可见问题的线索。
const debugLog: (...args: unknown[]) => void = import.meta.env.DEV
  ? (...args) => console.log(...args)
  : () => {};

export async function preloadContextAssets(
  context: Context,
  onProgress?: (progress: number) => void,
): Promise<void> {
  const lines = parseContextScript(context);
  const urls = collectPreloadAssetUrls(context, lines);
  const fonts = collectPreloadFonts(lines);

  // 进度按条数计：Assets.load 按已完成的资源条数回报，字体每款也算一条，
  // 全部完成才到 1。字体与图片/音频并行下载，loadFont 不会 reject。
  let assetsLoaded = 0;
  let fontsLoaded = 0;
  const reportProgress = () =>
    onProgress?.((assetsLoaded + fontsLoaded) / (urls.length + fonts.length));
  const fontsReady = Promise.all(
    fonts.map(async (font) => {
      await loadFont(font);
      fontsLoaded += 1;
      reportProgress();
    }),
  );

  debugLog(`[preload] ${urls.length} unique asset URL(s) collected`);
  if (urls.length === 0) {
    console.warn(
      "[preload] no assets resolved — script may have no collectable commands or resolvers returned empty",
    );
    await fontsReady;
    return;
  }

  // 打印前若干条 URL 便于核对路径前缀和大小写,过长则只展示首尾各几条。
  const preview =
    urls.length <= 8
      ? urls
      : [
          ...urls.slice(0, 5),
          `... (${urls.length - 7} more)`,
          ...urls.slice(-2),
        ];
  for (const [index, url] of preview.entries())
    debugLog(`[preload]   [${index}] ${url}`);

  let lastReported = -1;
  await Assets.load(urls, {
    onProgress: (progress) => {
      const percent = Math.round(progress * 100);
      // 只在整百分比变化时打印,避免 100 条资源刷屏。
      if (percent !== lastReported) {
        lastReported = percent;
        debugLog(
          `[preload] progress ${percent}% (${Math.round(progress * urls.length)}/${urls.length})`,
        );
      }
      assetsLoaded = progress * urls.length;
      reportProgress();
    },
    onError: (error, url) => {
      const urlStr =
        typeof url === "string"
          ? url
          : ((url as { src?: string })?.src ?? String(url));
      console.warn(
        `[preload] asset failed: ${urlStr}`,
        error?.message ?? error,
      );
    },
  });
  await fontsReady;
  debugLog(`[preload] done: ${urls.length} asset(s) resolved`);
}
